import React from 'react';

import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';

import { ModalBodyContainer, ModalItemContainer, ModalSubtitleContainer } from './styled';
import { IDeliveryFeesTier } from './types';

export const DeliveryFeesStripeModal: React.FC<React.PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  fees: IDeliveryFeesTier;
  smallCartFeeThreshold: number;
}>> = ({
  open,
  onClose,
  fees: { fee, serviceFeePercent, smallCartFee, geographicalFee },
  smallCartFeeThreshold,
}) => {
  const { formatCurrencyForLocale } = useUIContext();

  if (!open) {
    return null;
  }
  return (
    <Dialog
      showDialog={open}
      testID="delivery-fees-stripe-modal"
      heading={t('deliveryFees')}
      bodyComponent={
        <ModalBodyContainer>
          <ModalItemContainer testID="delivery-fee-item">
            <ModalSubtitleContainer>
              <Typography bold>{t('deliveryFee')}: </Typography>
              <Currency amount={fee} />
            </ModalSubtitleContainer>
            <Typography>{t('deliveryFeeDescription')}</Typography>
          </ModalItemContainer>
          {geographicalFee > 0 && (
            <ModalItemContainer testID="geographical-fee-item">
              <ModalSubtitleContainer>
                <Typography bold>{t('geographicalFee')}: </Typography>
                <Currency amount={geographicalFee} />
              </ModalSubtitleContainer>
              <Typography>
                {t('geographicalFeeDescription', {
                  geographicalFeeAmount: centsToDollarString(geographicalFee),
                })}
              </Typography>
            </ModalItemContainer>
          )}
          {serviceFeePercent > 0 && (
            <ModalItemContainer testID="service-fee-item">
              <ModalSubtitleContainer>
                <Typography bold>{t('serviceFee')}: </Typography>
                <Typography>{serviceFeePercent}%</Typography>
              </ModalSubtitleContainer>
              <Typography>
                {t('serviceFeeDescription', {
                  percent: serviceFeePercent,
                })}
              </Typography>
            </ModalItemContainer>
          )}
          {smallCartFee > 0 && (
            <ModalItemContainer testID="small-cart-fee-item">
              <ModalSubtitleContainer>
                <Typography bold>{t('smallCartFee')}: </Typography>
                <Currency amount={smallCartFee} />
              </ModalSubtitleContainer>
              {smallCartFee > 0 && (
                <Typography>
                  {t('smallCartFeeDescription', {
                    amount: formatCurrencyForLocale(smallCartFeeThreshold),
                  })}
                </Typography>
              )}
            </ModalItemContainer>
          )}
        </ModalBodyContainer>
      }
      actions={
        <ActionButton
          onPress={onClose}
          testID="delivery-fees-modal-ok-btn"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {t('okayButtonText')}
        </ActionButton>
      }
      modalAppearanceEventMessage="Delivery Fees Stripe Modal"
      onDismiss={onClose}
    />
  );
};
